import {
  Box,
  FormControl,
  FormLabel,
  TextField,
  FormHelperText,
  SelectChangeEvent,
  Fade,
  Typography,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import Autocomplete from "@mui/lab/Autocomplete";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { CurrencyConfigRes } from "src/store/features/api/generated";
import { AddressTestingStyles } from "../common/styles/AddressTesting.styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import React, { SetStateAction } from "react";

import { ActionCreatorWithPayload } from "@reduxjs/toolkit";
import { useAppDispatch } from "src/store/store";

type AddressFormProps = {
  selectedCurrency: CurrencyConfigRes | undefined;
  depositableCurrencies: CurrencyConfigRes[];
  handleCurrencyChange: (event: SelectChangeEvent<string>) => void;
  currencyError: string;
  address: string;
  handleNetworkChange: (event: SelectChangeEvent<string>) => void;
  handleAddressChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  addressWarning: string;
  setAddressWarning: React.Dispatch<SetStateAction<string>>;
  selectedNetwork: string | null;
  memo: string;
  handleMemoChange: ActionCreatorWithPayload<string, string>;
  walletNickname: string;
  walletNicknameWarning: string;
  handleNicknameChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
};

export const AddressForm = ({
  selectedCurrency,
  depositableCurrencies,
  handleCurrencyChange: originalHandleCurrencyChange,
  currencyError,
  address,
  handleAddressChange,
  addressWarning,
  memo,
  handleMemoChange,
  walletNickname,
  walletNicknameWarning,
  handleNicknameChange,
}: AddressFormProps) => {
  const classes = AddressTestingStyles();
  const dispatch = useAppDispatch();

  const handleCurrencyChange = (event: React.ChangeEvent<{}>, value: CurrencyConfigRes | null) => {
    if (value) {
      originalHandleCurrencyChange({
        target: { value: value.code },
      } as SelectChangeEvent<string>);
      dispatch(handleMemoChange(""));
    } else {
      originalHandleCurrencyChange({
        target: { value: "" },
      } as SelectChangeEvent<string>);
    }
  };

  const AutocompleteCurrency = ({
    depositableCurrencies,
    handleCurrencyChange,
  }: {
    depositableCurrencies: CurrencyConfigRes[];
    handleCurrencyChange: (
      event: React.ChangeEvent<{}>,
      value: CurrencyConfigRes | null,
      reason: string,
    ) => void;
  }) => (
    <Autocomplete
      className={classes.autocompleteInput}
      options={depositableCurrencies}
      getOptionLabel={(option) => option.name || option.code || ""}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <img
            src={option.logoUrl}
            alt={option.displayName}
            height="20"
            style={{ marginRight: "5px" }}
          />
          <span>{option.displayName}</span>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            startAdornment: selectedCurrency ? (
              <InputAdornment position="start">
                <img
                  src={selectedCurrency.logoUrl}
                  alt={selectedCurrency.displayName}
                  height="20"
                  style={{ marginRight: "5px" }}
                />
              </InputAdornment>
            ) : null,
          }}
        />
      )}
      onChange={handleCurrencyChange}
      value={selectedCurrency}
    />
  );

  return (
    <>
      <FormControl className={classes.formContainer}>
        <FormLabel className={classes.formLabels} id="currency-label">
          Select Currency
        </FormLabel>
        <AutocompleteCurrency
          depositableCurrencies={depositableCurrencies}
          handleCurrencyChange={handleCurrencyChange}
        />
        {currencyError && (
          <FormHelperText className={classes.formHelperText} error>
            <ErrorOutlineOutlinedIcon className={classes.errorIcon} />
            {currencyError}
          </FormHelperText>
        )}
      </FormControl>
      <FormControl className={classes.formContainer}>
        <FormLabel className={classes.formLabels}>Enter Address</FormLabel>
        <TextField
          className={classes.inputContainers}
          variant="outlined"
          id="crypto-address-input"
          value={address}
          placeholder="Wallet Address"
          onChange={(event) => {
            handleAddressChange(event);
          }}
          onKeyDown={(event) => {
            if (event.key === " ") {
              event.preventDefault();
            }
          }}
        />
        {addressWarning && (
          <FormHelperText className={classes.formHelperText}>
            <ErrorOutlineOutlinedIcon className={classes.warningIcon} />
            {addressWarning}
          </FormHelperText>
        )}
      </FormControl>
      <FormControl className={classes.formContainer}>
        <FormLabel className={classes.formLabels}>Wallet Nickname</FormLabel>
        <TextField
          className={classes.inputContainers}
          variant="outlined"
          id="crypto-address-nickname-input"
          value={walletNickname}
          placeholder="Pick a memorable name for your wallet"
          onChange={(event) => {
            handleNicknameChange(event);
          }}
        />
        {walletNicknameWarning && (
          <FormHelperText className={classes.formHelperText}>
            <ErrorOutlineOutlinedIcon className={classes.warningIcon} />
            {walletNicknameWarning}
          </FormHelperText>
        )}
      </FormControl>
      <FormControl className={classes.formContainer}>
        <FormLabel className={classes.formLabels} id="network-label">
          Address Type
        </FormLabel>
        <Typography sx={{ fontWeight: 700 }}>{selectedCurrency?.formatName}</Typography>
      </FormControl>
      {selectedCurrency && selectedCurrency.memo && (
        <Fade in={selectedCurrency.memo} mountOnEnter unmountOnExit>
          <FormControl className={classes.formContainer}>
            <Box display="flex" alignItems="center">
              <FormLabel className={classes.formLabels}>Memo</FormLabel>
              <Tooltip
                classes={{ tooltip: classes.tooltip }}
                placement="top-start"
                title="Missing or incorrect entry of MEMO may cause loss of your assets">
                <InfoOutlinedIcon
                  style={{
                    marginLeft: "8px",
                    fontSize: "14px",
                    marginBottom: "12px",
                  }}
                />
              </Tooltip>
            </Box>
            <TextField
              className={classes.inputContainers}
              variant="outlined"
              id="memo-input"
              value={memo}
              onChange={(event) => dispatch(handleMemoChange(event.target.value))}
            />
          </FormControl>
        </Fade>
      )}
    </>
  );
};
